import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';

const Navbar = ({ onBookClick, hideBookNow = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'How It Works', href: '/#how-it-works' },
    { name: 'Services', href: '/#services' },
    { name: 'Pricing', href: '/pricing', isRoute: true },
    { name: 'Blog', href: '/blog', isRoute: true },
    { name: 'About', href: '/#why-nura' },
    { name: 'Contact', href: '/#contact' },
  ];

  const handleNavClick = (e, href, isRoute) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);

    if (isRoute || href === '/') {
      window.location.href = href;
    } else if (href.startsWith('/#')) {
      const currentPath = window.location.pathname;
      if (currentPath === '/') {
        const anchor = '#' + href.split('#')[1];
        if (anchor === '#home') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          const element = document.querySelector(anchor);
          if (element) {
            const offset = 80;
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
          }
        }
      } else {
        window.location.href = href;
      }
    }
  };

  return (
    <>
      <nav
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: '#00001E',
          borderBottom: isScrolled
            ? '1px solid rgba(255,255,255,0.10)'
            : '1px solid rgba(255,255,255,0.06)',
          boxShadow: isScrolled ? '0 4px 24px rgba(0,0,30,0.4)' : 'none',
        }}
      >
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-20">

            {/* Logo */}
            <div className="flex-shrink-0">
              <a href="/" onClick={(e) => handleNavClick(e, '/')}>
                <img
                  src="/Nura_Final-04.png"
                  alt="Nura Health mobile pathology logo"
                  className="w-auto transition-all duration-300"
                  style={{ height: '48px', background: 'transparent', objectFit: 'contain' }}
                />
              </a>
            </div>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href, link.isRoute)}
                  className="relative text-sm font-medium transition-colors duration-200 group"
                  style={{ color: 'rgba(255,255,255,0.75)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#FFFFFF'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.75)'; }}
                >
                  {link.name}
                  <span
                    className="absolute bottom-0 left-0 w-0 h-0.5 transition-all duration-300 group-hover:w-full"
                    style={{ background: '#80E5CB' }}
                  ></span>
                </a>
              ))}
            </div>

            {/* CTA Button */}
            {!hideBookNow && (
              <div className="hidden md:block">
                <button
                  onClick={onBookClick}
                  style={{
                    background: '#80E5CB',
                    color: '#00001E',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 26px',
                    fontWeight: '700',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 16px rgba(128,229,203,0.30)',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 28px rgba(128,229,203,0.45)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 16px rgba(128,229,203,0.30)';
                  }}
                >
                  Book Now
                </button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              className="md:hidden transition-colors"
              style={{ color: 'rgba(255,255,255,0.85)' }}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay — dark */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          style={{ top: '80px', background: '#00001E', borderTop: '1px solid rgba(255,255,255,0.08)' }}
        >
          <div className="container mx-auto px-6 py-8">
            <div className="flex flex-col space-y-6">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href, link.isRoute)}
                  className="text-lg font-medium py-2 transition-colors duration-200"
                  style={{ color: 'rgba(255,255,255,0.80)' }}
                >
                  {link.name}
                </a>
              ))}
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  onBookClick();
                }}
                style={{
                  background: '#80E5CB',
                  color: '#00001E',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '14px 28px',
                  fontWeight: '700',
                  fontSize: '15px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                  cursor: 'pointer',
                  width: '100%',
                  marginTop: '1rem',
                }}
              >
                Book Now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
